import { Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { IPage } from '../../models';
import { LocalizationModel } from '../../localization';

@Injectable({
  providedIn: 'root',
})
export class PageService {
  private collectionName = 'pages';

  constructor(private firestore: Firestore) {}

  getById(id: string): Observable<IPage | undefined> {
    const pageRef = doc(this.firestore, this.collectionName, id);

    return new Observable<IPage | undefined>(observer => {
      const unsubscribe = onSnapshot(
        pageRef,
        snapshot => {
          if (snapshot.exists()) {
            const data = {
              id: snapshot.id,
              ...snapshot.data(),
            } as IPage;
            observer.next(data);
          } else {
            observer.next(undefined);
          }
        },
        error => {
          observer.error(error);
        },
      );

      return () => unsubscribe();
    });
  }

  getAll(): Observable<IPage[]> {
    const pagesRef = collection(this.firestore, this.collectionName);

    return new Observable<IPage[]>(observer => {
      // Подписываемся на изменения (Real-time)
      const unsubscribe = onSnapshot(
        pagesRef,
        snapshot => {
          // Преобразуем данные
          const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as IPage[];

          // Отправляем в поток
          observer.next(data);
        },
        error => {
          // Обрабатываем ошибки
          observer.error(error);
        },
      );

      // Функция очистки (вызывается, когда компонент уничтожается/отписывается)
      return () => unsubscribe();
    });
  }

  async create(data: Omit<IPage, 'id' | 'createdAt' | 'updatedAt' | 'order'>): Promise<string> {
    const pagesRef = collection(this.firestore, this.collectionName);
    const docData = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const docRef = await addDoc(pagesRef, docData);
    return docRef.id;
  }

  async update(id: string, data: Partial<IPage>): Promise<void> {
    const docRef = doc(this.firestore, this.collectionName, id);
    const updateData = {
      ...data,
      updatedAt: serverTimestamp(),
    };
    delete updateData.id;
    delete updateData.createdAt;
    await updateDoc(docRef, updateData);
  }

  async delete(id: string): Promise<void> {
    const docRef = doc(this.firestore, this.collectionName, id);
    await deleteDoc(docRef);
  }

  generateSlug(title: LocalizationModel): string {
    const titleString = title.en ?? title.ru ?? title.kz ?? '';
    return titleString
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Удаляем все символы, кроме букв, цифр и дефисов
      .replace(/[\s_-]+/g, '-') // Заменяем все пробелы, подчеркивания и дефисы на дефисы
      .replace(/^-+|-+$/g, ''); // Удаляем дефисы в начале и конце
  }
}
